import { type PropsWithChildren } from 'react'
import { Box } from '../../primitives/Box'
import { type DataTestIdAttribute } from '../../types'
import { type CardState } from './Card/CardProvider'
import { useCard } from './use-card'

export type CardContentProps = PropsWithChildren<{
  /**
   * You need the complete section
   * This will overrule any other noPadding setting
   */
  noPaddings?: boolean

  /**
   * Special condition where you do not need the spacing to the top
   */
  noPaddingTop?: boolean

  /**
   * Special condition where you do not need the spacing to the bottom
   */
  noPaddingBottom?: boolean

  /**
   * Special condition where you do not need the spacing to the sides
   */
  noPaddingSides?: boolean
}> &
  DataTestIdAttribute

const getStyles = ({
  isDense,
  spacingToken,
  noPaddings,
  noPaddingSides,
  noPaddingTop,
  noPaddingBottom,
}: Pick<CardState, 'isDense'> & Pick<CardState, 'spacingToken'> & CardContentProps) => {
  if (noPaddings || (noPaddingSides && noPaddingTop && noPaddingBottom)) {
    return {
      flex: '1',
      minHeight: 0,
    }
  }

  const px = isDense ? 2 : spacingToken
  const pt = noPaddingTop ? 0 : spacingToken
  const pb = noPaddingBottom ? 0 : spacingToken

  return { flex: '1', minHeight: 0, px: noPaddingSides ? 0 : px, pt, pb }
}

export const BaseCardContent = ({ children, 'data-testid': testId, ...rest }: CardContentProps) => {
  const { isDense, spacingToken } = useCard()
  const sx = getStyles({ isDense, spacingToken, ...rest })

  return (
    <Box sx={sx} data-testid={testId}>
      {children}
    </Box>
  )
}
